import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useProfiles, useDeleteProfile } from '@/lib/sdk/profiling/queries';
import type { Profile, ListProfilesParams } from '@/lib/sdk/profiling/types';

export function useProfileListPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [deletingProfile, setDeletingProfile] = useState<Profile | null>(null);
  const [filters, setFilters] = useState<ListProfilesParams>({ limit: 20, offset: 0 });

  const { data: profilesData, isLoading } = useProfiles(filters);
  const { mutate: deleteProfile, isPending: isDeleting } = useDeleteProfile();

  const isSuperAdmin = user?.is_super_admin === true;

  const handleDeleteClick = (profile: Profile) => {
    setDeletingProfile(profile);
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (!deletingProfile) return;
    deleteProfile(deletingProfile.id, {
      onSuccess: () => { toast.success('Profile deleted successfully'); onDeleteClose(); setDeletingProfile(null); },
      onError: (error: Error) => toast.error(error.message || 'Failed to delete profile'),
    });
  };

  return {
    orgId, navigate, isSuperAdmin,
    profilesData, isLoading,
    filters, setFilters,
    deletingProfile, setDeletingProfile,
    isDeleting,
    isDeleteOpen, onDeleteOpen, onDeleteClose,
    handleDeleteClick, handleConfirmDelete,
  };
}
